/**
 * Development View cluster for the Workspace Overview / Portfolio module.
 *
 * v260.76 — extracted from WorkspaceOverviewPage.js. Contains:
 *   • DevelopmentView      (the tab body)
 *   • DevRow               (one project row + its lane-packed sub-rows)
 *   • DevSplitDonut        (mini donut per project)
 *   • PortfolioDevSplit    (top summary card)
 *   • DevMixTrendChip      (period-over-period delta chip)
 * Imports DEV_TYPE / fmtDate / WeekHeader / EmptyState / buildDevPlanCsvRows
 * / csvEscape from ./shared so they remain single-sourced.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Building2, ChevronRight, ChevronDown, Download,
  ArrowUp, ArrowDown, Minus, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { toast } from 'sonner';
import logger from '../../lib/logger';
import { useAuth } from '../../context/AuthContext';
import {
  DEV_TYPE, fmtDate, WeekHeader, EmptyState, HighlightMatch,
  csvEscape, buildDevPlanCsvRows,
  buildReleaseCols, releaseColsByIdx, RELEASE_COLORS,
} from './shared';

function DevelopmentView({ data, weekLabels, todayIdx, priorDevSplit, compareMode, onCompareModeChange, onOpen, riskOnly = false, searchQuery = '' }) {
  // v260.69 / v260.70 — Expand/collapse state, persisted across
  // reloads in localStorage so consultants keep their layout across
  // page refreshes. Keyed by user id so two consultants sharing a
  // browser profile don't stomp each other's preference.
  //
  // ⚠️ The race we hit twice (iter_233, iter_234): AuthContext
  // resolves `user` asynchronously, so on the first render `user.id`
  // may be undefined. Once it arrives, the persist useEffect would
  // fire with the still-empty pre-hydration `collapsed` (stale
  // closure!) and wipe the saved value before the hydration
  // setState had a chance to apply.
  //
  // The clean fix is to gate persistence on **user intent**, not on
  // hydration timing: a `userTouchedRef` flipped to true ONLY inside
  // the click handlers. The persist effect is then a no-op until the
  // user actually changes something, by which point `collapsed` and
  // the user's intent are in sync — and the hydration effect can
  // populate `collapsed` from localStorage without ever risking a
  // wipe.
  const { user } = useAuth();
  const storageKey = user?.id ? `dev_collapsed_projects_${user.id}` : null;

  const [collapsed, setCollapsed] = useState(() => new Set());
  const userTouchedRef = useRef(false);

  // Hydrate from localStorage once we know the user id.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      setCollapsed(new Set((raw || '').split(',').filter(Boolean)));
    } catch (err) {
      // localStorage disabled — keep empty Set, no persistence.
      console.warn('DevelopmentView: localStorage read failed; persistence disabled', err);
    }
  }, [storageKey]);

  const toggleProject = useCallback((pid) => {
    userTouchedRef.current = true;
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  }, []);

  // Memoise rows so the hooks below see a stable reference across
  // renders that don't actually change the project list.
  const rows = useMemo(
    () => (data?.projects || [])
      .filter(p => (p.dev_phases || []).length > 0)
      .filter(p => !riskOnly || (p.dev_phases || []).some(ph => ph.release_at_risk)),
    [data, riskOnly],
  );

  // v260.136 — Public-Cloud release columns for the timeline markers.
  const releaseCols = useMemo(() => buildReleaseCols(weekLabels, data?.releases), [weekLabels, data]);
  const releaseByIdx = useMemo(() => releaseColsByIdx(releaseCols), [releaseCols]);

  // Prune `collapsed` to the current row set whenever the filter
  // changes — keeps `allCollapsed` accurate after a status / date
  // filter narrows or widens scope. Skip the prune entirely until
  // `rows` is populated, otherwise the very first render after a
  // reload (when `data` is still loading) would prune the hydrated
  // ids to an empty Set.
  const rowIdsKey = rows.map(r => r.id).join('|');
  useEffect(() => {
    if (rows.length === 0) return;
    const ids = new Set(rows.map(r => r.id));
    setCollapsed(prev => {
      let changed = false;
      const next = new Set();
      for (const pid of prev) {
        if (ids.has(pid)) next.add(pid); else changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIdsKey]);

  // Persist on every change — but ONLY after the user has actually
  // touched the state. This guarantees the persist effect cannot
  // fire with a stale-closure empty Set during initial hydration.
  useEffect(() => {
    if (!storageKey || !userTouchedRef.current || typeof window === 'undefined') return;
    try {
      if (collapsed.size === 0) {
        window.localStorage.removeItem(storageKey);
      } else {
        window.localStorage.setItem(storageKey, [...collapsed].join(','));
      }
    } catch (err) {
      // localStorage quota or disabled — silent, this is a UX
      // convenience, not a critical write.
      console.warn('DevelopmentView: localStorage write failed', err);
    }
  }, [collapsed, storageKey]);

  if (!data) return null;
  if (rows.length === 0) return <EmptyState message="No development, integration or migration phases in the selected window." />;

  // Portfolio-level roll-up across every classified band in scope.
  const portfolioSplit = rows.reduce((acc, p) => {
    for (const ph of (p.dev_phases || [])) {
      const t = ph.type || 'other';
      acc[t] = (acc[t] || 0) + (ph.hours || 0);
    }
    return acc;
  }, {});
  const portfolioTotal = Object.values(portfolioSplit).reduce((s, v) => s + v, 0);

  const allCollapsed = collapsed.size === rows.length;
  const handleCollapseAll = () => {
    userTouchedRef.current = true;
    setCollapsed(new Set(rows.map(r => r.id)));
  };
  const handleExpandAll = () => {
    userTouchedRef.current = true;
    setCollapsed(new Set());
  };

  // v260.72 — CSV "Export development plan". Pure-frontend: data is
  // already in scope (no extra API round-trip) and the upstream
  // filters (status / lifecycle / date window) have already been
  // applied. One row per classified band so finance / capacity
  // planners can drop the file straight into a pivot table.
  const handleExportDevPlan = () => {
    try {
      const rowMatrix = buildDevPlanCsvRows(data);
      const csv = rowMatrix.map(r => r.map(csvEscape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const range = `${data?.date_range?.start || 'start'}-to-${data?.date_range?.end || 'end'}`;
      a.href = url;
      a.download = `seertech-development-plan-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Development plan exported (${rows.length} projects, ${Math.round(portfolioTotal).toLocaleString()} h)`);
    } catch (e) {
      logger.warn('dev-plan csv export failed', e);
      toast.error('Could not export development plan');
    }
  };

  return (
    <>
      <PortfolioDevSplit
        split={portfolioSplit}
        total={portfolioTotal}
        projectCount={rows.length}
        priorSplit={priorDevSplit}
        compareMode={compareMode}
        onCompareModeChange={onCompareModeChange}
      />

      {/* v260.69 — Legend + expand/collapse toolbar moved ABOVE the
          timeline so consultants don't have to scroll past the data
          to learn what the colours mean. Each legend chip now also
          surfaces the portfolio-wide hour rollup for that type so
          the band itself answers "how much work is in this category
          across the whole book". */}
      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap" data-testid="overview-development-legend-toolbar">
        <div className="flex items-center gap-3 text-[11px] text-slate-600">
          <span className="font-semibold text-[#0F2040] uppercase tracking-wide text-[10px]">Legend</span>
          {Object.entries(DEV_TYPE).map(([k, v]) => {
            const hrs = portfolioSplit[k] || 0;
            return (
              <span key={k} className="inline-flex items-center gap-1.5" data-testid={`overview-development-legend-${k}`}>
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: v.color }} />
                <span>{v.label}</span>
                <span className="text-slate-400 tabular-nums">· {hrs.toLocaleString()} h</span>
              </span>
            );
          })}
          {releaseCols.length > 0 && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-slate-400 uppercase tracking-wide text-[10px]">Releases</span>
              {['prod', 'test', 'maintenance'].map(rt => (
                <span key={rt} className="inline-flex items-center gap-1" data-testid={`overview-development-release-legend-${rt}`}>
                  <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: RELEASE_COLORS[rt] }} />
                  <span>{{ prod: 'Full', test: 'Test', maintenance: 'Maint.' }[rt]}</span>
                </span>
              ))}
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleExportDevPlan}
            className="text-[10px] font-medium text-[#0F2040] hover:bg-[#4ABFCF]/10 border border-slate-200 rounded-md px-2 py-1 flex items-center gap-1 transition"
            data-testid="overview-development-export-csv"
            title="Download a CSV with one row per development item (Project, Type, Item, Group, Start, End, Hours)"
          >
            <Download className="w-3 h-3" /> Export CSV
          </button>
          <button
            type="button"
            onClick={allCollapsed ? handleExpandAll : handleCollapseAll}
            className="text-[10px] font-medium text-[#0F2040] hover:bg-[#4ABFCF]/10 border border-slate-200 rounded-md px-2 py-1 flex items-center gap-1 transition"
            data-testid="overview-development-toggle-all"
            title={allCollapsed ? 'Expand every project' : 'Collapse every project to the total bar'}
          >
            {allCollapsed
              ? <><ChevronDown className="w-3 h-3" /> Expand all</>
              : <><ChevronRight className="w-3 h-3" /> Collapse all</>}
          </button>
        </div>
      </div>

      <Card className="mt-2"><CardContent className="p-0 overflow-x-auto" data-testid="overview-development-view">
        <table className="w-full text-xs">
          <WeekHeader weekLabels={weekLabels} todayIdx={todayIdx} firstColLabel="Project" firstColWidth="280px" releaseCols={releaseCols} />
          <tbody>
            {rows.map((p) => (
              <DevRow
                key={p.id}
                p={p}
                weekLabels={weekLabels}
                todayIdx={todayIdx}
                onOpen={onOpen}
                expanded={!collapsed.has(p.id)}
                onToggle={() => toggleProject(p.id)}
                releaseByIdx={releaseByIdx}
                searchQuery={searchQuery}
              />
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </>
  );
}

function DevRow({ p, weekLabels, todayIdx, onOpen, expanded = true, onToggle, releaseByIdx = {}, searchQuery = '' }) {
  // v260.66 — Re-laid-out per-project block:
  //   ① Project header row with donut + a single neutral bar showing
  //      the overall development window (earliest start → latest end
  //      across every classified band on the project).
  //   ② One indented sub-row per dev TYPE that actually has bands on
  //      this project (New Development / Integration / Data Migration),
  //      rendering only that type's bands in its own colour. This
  //      replaces the previous "all bands stacked on a single row"
  //      view, which crammed three types into a 12-px vertical strip
  //      and forced the reader to decode by hue.
  const phases = p.dev_phases || [];
  const split = phases.reduce((acc, ph) => {
    const t = ph.type || 'other';
    acc[t] = (acc[t] || 0) + (ph.hours || 0);
    return acc;
  }, {});
  const totalHrs = Object.values(split).reduce((s, v) => s + v, 0);

  // Group phases by type and preserve the canonical render order
  // (new_dev → integration → migration → other) so the indented stack
  // reads consistently across projects.
  const TYPE_ORDER = ['new_dev', 'integration', 'migration', 'other'];
  const byType = phases.reduce((acc, ph) => {
    const t = ph.type || 'other';
    (acc[t] = acc[t] || []).push(ph);
    return acc;
  }, {});
  const orderedTypes = TYPE_ORDER.filter(t => (byType[t] || []).length > 0);

  // Overall development window across every band on the project.
  const allStarts = phases.map(ph => ph.start_date).filter(Boolean).sort();
  const allEnds = phases.map(ph => ph.end_date).filter(Boolean).sort();
  const totalStart = allStarts[0];
  const totalEnd = allEnds[allEnds.length - 1];

  const cellWidthPx = 18;

  // v260.137 — Count deliverables that slip past their anchored release.
  const atRiskCount = phases.filter(ph => ph.release_at_risk).length;

  // v260.136 — Public-Cloud release rule for a given column index.
  const relRule = (i) => (releaseByIdx[i] || []).map((r, k) => (
    <div
      key={r.id || `rel-${i}-${k}`}
      className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px z-0 pointer-events-none"
      style={{ background: RELEASE_COLORS[r.release_type] || '#64748B', opacity: 0.4 }}
      title={`${r.label || 'Release'}${r.date ? ` · ${r.date}` : ''}`}
    />
  ));

  return (
    <>
      {/* ─── Project header row (total dev window) ────────────── */}
      <tr className="border-t border-slate-200 hover:bg-slate-50" data-testid={`dev-row-${p.id}`}>
        <td className="px-3 py-2 sticky left-0 bg-white border-r border-slate-100">
          <div className="flex items-center gap-1.5">
            {/* v260.69 — Chevron to collapse/expand the per-type
                sub-rows for this project. Keeps the same data-testid
                affordances when collapsed (sub-row testIds simply
                aren't rendered), and the project total bar always
                stays visible so the glanceable view never loses the
                "which projects are doing dev work" signal. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggle && onToggle(); }}
              className="text-slate-400 hover:text-[#0F2040] shrink-0 -ml-1 p-0.5 rounded transition"
              data-testid={`dev-row-toggle-${p.id}`}
              title={expanded ? 'Collapse this project' : 'Expand to see development items'}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => onOpen(p.id)} className="text-left flex-1 min-w-0 group flex items-center gap-2">
              <DevSplitDonut split={split} total={totalHrs} />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#0F2040] text-xs flex items-center gap-1 group-hover:text-[#4ABFCF]">
                  <Building2 className="w-3 h-3 shrink-0 text-slate-400" />
                  <span className="truncate"><HighlightMatch text={p.name} query={searchQuery} /></span>
                  {atRiskCount > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 shrink-0 text-[9px] font-semibold text-rose-700 bg-rose-100 border border-rose-200 rounded px-1 py-px"
                      title={`${atRiskCount} deliverable${atRiskCount === 1 ? '' : 's'} slip past the anchored release date`}
                      data-testid={`dev-row-risk-badge-${p.id}`}
                    >
                      <AlertTriangle className="w-2.5 h-2.5" />
                      {atRiskCount}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {totalHrs.toLocaleString()} h total across {orderedTypes.length} type{orderedTypes.length === 1 ? '' : 's'}
                </div>
              </div>
            </button>
          </div>
        </td>
        {weekLabels.map((wkLabel, i) => {
          // Single neutral bar from the project's earliest start to
          // its latest end — drawn only at the leftmost cell.
          if (!totalStart || !totalEnd) return (
            <td key={wkLabel || i} className={`${i === todayIdx ? 'bg-amber-50/60' : ''}`} style={{ width: cellWidthPx }} />
          );
          const ps = weekLabels.findIndex(w => w >= totalStart);
          const pe = weekLabels.findIndex(w => w >= totalEnd);
          const left = ps < 0 ? 0 : ps;
          const right = pe < 0 ? weekLabels.length - 1 : pe;
          return (
            <td key={wkLabel || i} className={`relative ${i === todayIdx ? 'bg-amber-50/60' : ''}`} style={{ width: cellWidthPx }}>
              {relRule(i)}
              {i === left && (
                <div
                  className="absolute h-3 rounded-sm"
                  style={{
                    left: 1,
                    width: `${(right - left + 1) * cellWidthPx - 2}px`,
                    top: 4,
                    background: '#334155',
                    opacity: 0.85,
                  }}
                  title={`Overall development window: ${fmtDate(totalStart)} → ${fmtDate(totalEnd)} · ${totalHrs.toLocaleString()} h`}
                />
              )}
            </td>
          );
        })}
      </tr>

      {/* ─── Indented sub-rows: one per dev type with bands ──── */}
      {expanded && orderedTypes.map(type => {
        const typePhases = byType[type];
        const typeHrs = split[type] || 0;
        const meta = DEV_TYPE[type] || { label: type, color: '#94A3B8' };
        // v260.68 — Lane-pack bands so each item visually owns its
        // own row when it overlaps another in time. Bands that don't
        // overlap can share a lane (compact). Greedy interval-
        // scheduling: scan bands in start-date order, drop each into
        // the first lane whose last band ends before this one starts;
        // open a new lane otherwise. Each lane gets a fixed 16px slot
        // (10px bar + 6px gap), and the sub-row td height grows to
        // fit `laneCount` lanes — so two parallel integrations always
        // render as two clearly-separated bars rather than the
        // previous semi-overlapping "blob".
        //
        // Algorithm requires ISO-8601 (YYYY-MM-DD) date strings so
        // that lexicographic comparison matches chronological order.
        // Phases missing either date are pushed to the end of the
        // sort order and always open a fresh lane (defensive — the
        // upstream `_extract_dev_phases` only emits ISO dates today).
        const laneEnds = []; // laneEnds[i] = end_date of last band placed in lane i
        const laneByPhase = new Map();
        const phasesByStart = [...typePhases].sort((a, b) => {
          const sa = a.start_date || '';
          const sb = b.start_date || '';
          if (!sa && !sb) return 0;
          if (!sa) return 1;
          if (!sb) return -1;
          return sa.localeCompare(sb);
        });
        for (const ph of phasesByStart) {
          const start = ph.start_date || '';
          const end = ph.end_date || '';
          let placed = false;
          if (start && end) {
            for (let i = 0; i < laneEnds.length; i++) {
              if (laneEnds[i] && laneEnds[i] < start) {
                laneEnds[i] = end;
                laneByPhase.set(ph, i);
                placed = true;
                break;
              }
            }
          }
          if (!placed) {
            laneByPhase.set(ph, laneEnds.length);
            laneEnds.push(end);
          }
        }
        const laneCount = Math.max(laneEnds.length, 1);
        const LANE_HEIGHT = 16; // 10px bar + 6px gap — clear visual separation
        const rowHeight = 8 + laneCount * LANE_HEIGHT;
        return (
          <tr key={`${p.id}-${type}`} className="bg-slate-50/30 border-t border-slate-100" data-testid={`dev-subrow-${p.id}-${type}`}>
            <td className="px-3 py-1.5 sticky left-0 bg-slate-50/40 border-r border-slate-100" style={{ height: rowHeight }}>
              <div className="flex items-center gap-2 pl-7">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: meta.color }} />
                <div className="text-[11px] text-slate-600 flex-1 truncate">{meta.label}</div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* Bumped from text-[10px] / slate-400 → text-[11px]
                      semibold / slate-700 so the per-type total reads
                      as the "fact" of the row, not a footnote. */}
                  <div className="text-[11px] font-semibold text-slate-700 tabular-nums" data-testid={`dev-subrow-hours-${p.id}-${type}`}>
                    {typeHrs.toLocaleString()} h
                  </div>
                  {/* Show the count when more than one item shares this row so consultants instantly know how many distinct items are packed in. */}
                  {typePhases.length > 1 && (
                    <span
                      className="text-[9px] text-slate-500 bg-slate-200/60 rounded px-1 py-px tabular-nums font-medium"
                      title={`${typePhases.length} ${meta.label.toLowerCase()} items`}
                      data-testid={`dev-subrow-count-${p.id}-${type}`}
                    >
                      ×{typePhases.length}
                    </span>
                  )}
                </div>
              </div>
            </td>
            {weekLabels.map((wkLabel, i) => (
              <td
                key={wkLabel || i}
                className={`relative ${i === todayIdx ? 'bg-amber-50/40' : ''}`}
                style={{ width: cellWidthPx, height: rowHeight }}
              >
                {relRule(i)}
                {typePhases.map((ph, j) => {
                  const ps = weekLabels.findIndex(w => w >= ph.start_date);
                  const pe = weekLabels.findIndex(w => w >= ph.end_date);
                  const left = ps < 0 ? 0 : ps;
                  const right = pe < 0 ? weekLabels.length - 1 : pe;
                  if (i !== left) return null;
                  const bandWidth = (right - left + 1) * cellWidthPx - 2;
                  // Anchor the hover card to the band's right edge by
                  // default, flipping to the left edge when the band
                  // starts in the last 25% of the timeline so the card
                  // doesn't get cut off by the scroll container.
                  const flipLeft = left > weekLabels.length * 0.75;
                  const lane = laneByPhase.get(ph) ?? 0;
                  return (
                    <div
                      key={`${ph.type}-${ph.label}-${ph.start_date}-${ph.end_date}-${j}`}
                      className={`group/band absolute z-10 h-2.5 rounded-sm cursor-pointer hover:ring-2 hover:ring-white hover:ring-offset-1 hover:ring-offset-slate-100 hover:z-20 transition shadow-sm ${ph.release_at_risk ? 'ring-2 ring-rose-500 ring-offset-1 ring-offset-white' : ''}`}
                      style={{
                        left: 1,
                        width: `${bandWidth}px`,
                        top: 4 + lane * LANE_HEIGHT,
                        background: meta.color,
                        // Subtle outline keeps adjacent same-lane bands
                        // visually distinct when they touch end-to-end.
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35), 0 1px 1px rgba(15,32,64,0.08)',
                      }}
                      data-testid={`dev-band-${p.id}-${type}-${j}`}
                    >
                      {/* v260.137 — Release-risk marker: red dot at the
                          band's end when the deliverable slips past its
                          anchored release date. */}
                      {ph.release_at_risk && (
                        <span
                          className="absolute -right-1 -top-1 w-2 h-2 rounded-full bg-rose-500 ring-1 ring-white z-30"
                          data-testid={`dev-band-risk-${p.id}-${type}-${j}`}
                          title={`Slips ${ph.release_slip_days}d past ${ph.anchor_release_label}`}
                        />
                      )}
                      {/* v260.69 — Inside-band label when there's
                          enough horizontal room. Lets leadership scan
                          "SSO", "Workday HRIS", "SCORM Cloud" etc.
                          without hovering. Threshold ~80 px keeps
                          short bands clean (label would be too long
                          to fit anyway). */}
                      {bandWidth >= 80 && ph.label && (
                        <span
                          className="absolute inset-0 px-1.5 flex items-center text-white font-medium pointer-events-none truncate"
                          style={{ fontSize: '9px', lineHeight: '10px', textShadow: '0 1px 1px rgba(0,0,0,0.25)' }}
                          data-testid={`dev-band-label-${p.id}-${type}-${j}`}
                        >
                          {ph.label}
                        </span>
                      )}
                      {/* v260.67 — Inline hover card. Tells leadership
                          *which* integration / new-dev / migration this
                          band actually is (e.g. "SFTP → S3 connector"
                          vs "SCORM Cloud webhook") without leaving the
                          Portfolio view. pointer-events-none so the
                          card doesn't trap the hover when the cursor
                          drifts onto it. */}
                      <div
                        className={`hidden group-hover/band:block absolute z-30 pointer-events-none w-60 rounded-md shadow-lg border border-slate-200 bg-white p-2.5 text-[11px] ${flipLeft ? 'right-0' : 'left-0'}`}
                        style={{ top: -8, transform: `translateY(-100%) translateX(${flipLeft ? '12px' : '-12px'})` }}
                        data-testid={`dev-band-hover-${p.id}-${type}-${j}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="w-2 h-2 rounded-sm" style={{ background: meta.color }} />
                          <span className="font-semibold text-[#0F2040] text-[10px] uppercase tracking-wide">{meta.label}</span>
                        </div>
                        <div className="text-[#0F2040] font-medium leading-snug mb-1">{ph.label || 'Unnamed'}</div>
                        {ph.group && (
                          <div className="text-slate-400 text-[10px] mb-1.5 italic">From: {ph.group}</div>
                        )}
                        <div className="flex items-center justify-between gap-2 text-slate-600">
                          <span className="tabular-nums">{fmtDate(ph.start_date)} → {fmtDate(ph.end_date)}</span>
                          {ph.hours ? <span className="font-medium text-[#0F2040] tabular-nums">{ph.hours.toLocaleString()} h</span> : null}
                        </div>
                        {ph.anchor_release_label && (
                          <div className={`mt-1.5 pt-1.5 border-t border-slate-100 text-[10px] flex items-center gap-1 ${ph.release_at_risk ? 'text-rose-600 font-medium' : 'text-slate-500'}`}>
                            <AlertTriangle className={`w-3 h-3 shrink-0 ${ph.release_at_risk ? '' : 'opacity-0'}`} />
                            {ph.release_at_risk
                              ? `At risk — slips ${ph.release_slip_days}d past ${ph.anchor_release_label}`
                              : `Ships in ${ph.anchor_release_label}`}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}

// Stacked-donut chart of the new_dev / integration / migration hour
// split for a single project. Renders as a 28×28 SVG with proportional
// arcs in the same DEV_TYPE colours so the inline view stays consistent
// with the band legend. Hover surfaces per-slice hour breakdown.
function DevSplitDonut({ split, total }) {
  if (!total) {
    return <div className="w-7 h-7 rounded-full border-2 border-dashed border-slate-200 shrink-0" title="No classified hours" />;
  }
  const size = 28;
  const r = 11;
  const cx = size / 2;
  const cy = size / 2;
  // Order slices so larger arcs render first → small slivers stay on top
  const order = ['new_dev', 'integration', 'migration'];
  const slices = order
    .filter(k => (split[k] || 0) > 0)
    .map(k => ({ key: k, value: split[k] || 0, color: DEV_TYPE[k]?.color || '#94A3B8', label: DEV_TYPE[k]?.label || k }))
    .sort((a, b) => b.value - a.value);

  // Arc path helper — uses two cubic-equivalent SVG arc segments for slices
  // that need to wrap past 180°.
  const arcPath = (startAngle, endAngle) => {
    const sa = (startAngle - 90) * Math.PI / 180;
    const ea = (endAngle - 90) * Math.PI / 180;
    const x1 = cx + r * Math.cos(sa);
    const y1 = cy + r * Math.sin(sa);
    const x2 = cx + r * Math.cos(ea);
    const y2 = cy + r * Math.sin(ea);
    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  let cursor = 0;
  const hoverTitle = slices.map(s => `${s.label}: ${Math.round(s.value)}h (${Math.round((s.value / total) * 100)}%)`).join(' · ');

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      data-testid="dev-split-donut"
      role="img"
      aria-label={hoverTitle}
    >
      <title>{hoverTitle}</title>
      {slices.length === 1 ? (
        <circle cx={cx} cy={cy} r={r} fill={slices[0].color} />
      ) : (
        slices.map((s) => {
          const angle = (s.value / total) * 360;
          const startAngle = cursor;
          const endAngle = cursor + angle;
          cursor = endAngle;
          return <path key={s.key} d={arcPath(startAngle, endAngle)} fill={s.color} />;
        })
      )}
      {/* Donut hole keeps it visually distinct from a pie */}
      <circle cx={cx} cy={cy} r={5} fill="#FFFFFF" />
    </svg>
  );
}

// Portfolio-level dev-mix banner. Same DEV_TYPE palette + arc maths as
// the per-row donut, just sized up with a side legend showing absolute
// hours, % share, and project count. Sits above the table in the
// Development view so leadership reads it before scanning bands.
function PortfolioDevSplit({ split, total, projectCount, priorSplit, compareMode, onCompareModeChange }) {
  if (!total) return null;
  const size = 64;
  const r = 26;
  const cx = size / 2;
  const cy = size / 2;
  const order = ['new_dev', 'integration', 'migration'];
  const slices = order
    .filter(k => (split[k] || 0) > 0)
    .map(k => ({ key: k, value: split[k] || 0, color: DEV_TYPE[k]?.color || '#94A3B8', label: DEV_TYPE[k]?.label || k }))
    .sort((a, b) => b.value - a.value);

  const arcPath = (startAngle, endAngle) => {
    const sa = (startAngle - 90) * Math.PI / 180;
    const ea = (endAngle - 90) * Math.PI / 180;
    const x1 = cx + r * Math.cos(sa);
    const y1 = cy + r * Math.sin(sa);
    const x2 = cx + r * Math.cos(ea);
    const y2 = cy + r * Math.sin(ea);
    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  const dominant = slices[0];
  const dominantPct = Math.round((dominant.value / total) * 100);

  // Per-slice trend vs prior window. Compare % share (not raw hours) so a
  // bigger overall portfolio doesn't fake a "rising" trend on each slice.
  // priorSplit comes from a sibling endpoint call against [now - span, now);
  // null when fetch failed or not yet loaded.
  let trend = null;
  if (priorSplit) {
    const priorTotal = Object.values(priorSplit).reduce((s, v) => s + (v || 0), 0);
    if (priorTotal > 0) {
      const priorDominantHrs = priorSplit[dominant.key] || 0;
      const priorPct = Math.round((priorDominantHrs / priorTotal) * 100);
      const delta = dominantPct - priorPct;
      trend = { delta, priorPct };
    }
  }

  let cursor = 0;
  return (
    <Card className="mt-2" data-testid="overview-portfolio-dev-split">
      <CardContent className="p-3 flex items-center gap-4">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" role="img"
          aria-label={`Portfolio dev mix: ${slices.map(s => `${s.label} ${Math.round((s.value / total) * 100)}%`).join(', ')}`}>
          {slices.length === 1 ? (
            <circle cx={cx} cy={cy} r={r} fill={slices[0].color} />
          ) : (
            slices.map((s) => {
              const angle = (s.value / total) * 360;
              const startAngle = cursor;
              const endAngle = cursor + angle;
              cursor = endAngle;
              return <path key={s.key} d={arcPath(startAngle, endAngle)} fill={s.color} />;
            })
          )}
          <circle cx={cx} cy={cy} r={11} fill="#FFFFFF" />
        </svg>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Portfolio dev mix</div>
          <div className="text-sm text-[#0F2040] font-medium flex items-center gap-2 flex-wrap" data-testid="overview-portfolio-headline">
            <span>
              <span className="text-base font-bold">{dominantPct}%</span> {dominant.label.toLowerCase()}
            </span>
            {trend && <DevMixTrendChip delta={trend.delta} priorPct={trend.priorPct} sliceLabel={dominant.label} compareMode={compareMode} />}
            {onCompareModeChange && (
              <Select value={compareMode} onValueChange={onCompareModeChange}>
                <SelectTrigger className="h-6 w-auto min-w-[150px] text-[10px] px-2 py-0 border-slate-200 bg-white" data-testid="overview-compare-mode-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="text-xs">
                  <SelectItem value="prior_window" data-testid="overview-compare-prior-window">vs prior window (same length)</SelectItem>
                  <SelectItem value="prior_26w" data-testid="overview-compare-prior-26w">vs prior 26 weeks</SelectItem>
                  <SelectItem value="yoy" data-testid="overview-compare-yoy">vs same window last year (YoY)</SelectItem>
                </SelectContent>
              </Select>
            )}
            <span className="text-slate-400 font-normal"> · {Math.round(total).toLocaleString()} dev/int/mig hrs across {projectCount} project{projectCount === 1 ? '' : 's'}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px]" data-testid="overview-portfolio-legend">
            {slices.map((s) => {
              const pct = Math.round((s.value / total) * 100);
              return (
                <div key={s.key} className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                  <span className="text-slate-700 font-medium">{s.label}</span>
                  <span className="tabular-nums text-slate-500">{Math.round(s.value).toLocaleString()}h · {pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Small chip rendered inline with the dominant-slice headline. Shows the
// percentage-point shift vs the prior same-length window with a directional
// arrow. We treat ±1pp as "flat" (within rounding noise) to avoid jitter.
function DevMixTrendChip({ delta, priorPct, sliceLabel, compareMode = 'prior_window' }) {
  const abs = Math.abs(delta);
  let Icon = Minus;
  let tone = 'bg-slate-100 text-slate-600 border-slate-200';
  let sign = '';
  if (delta > 1) {
    Icon = ArrowUp;
    tone = 'bg-rose-50 text-rose-700 border-rose-200';
    sign = '+';
  } else if (delta < -1) {
    Icon = ArrowDown;
    tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    sign = '−';
  }
  const compareLabelShort = {
    prior_window: 'vs prior',
    prior_26w: 'vs prior 26w',
    yoy: 'vs YoY',
  }[compareMode] || 'vs prior';
  const compareLabelFull = {
    prior_window: 'the prior same-length window',
    prior_26w: 'the prior 26 weeks',
    yoy: 'the same window one year ago',
  }[compareMode] || 'the prior window';
  return (
    <span
      data-testid="overview-portfolio-trend"
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-medium ${tone}`}
      title={`${sliceLabel} share: ${priorPct}% in ${compareLabelFull} → now ${priorPct + delta}% (${delta >= 0 ? '+' : ''}${delta}pp)`}
    >
      <Icon className="w-3 h-3" />
      <span>{sign}{abs}pp {compareLabelShort}</span>
    </span>
  );
}

// ─── Shared bits ──────────────────────────────────────────────────────


export {
  DevelopmentView,
  DevRow,
  DevSplitDonut,
  PortfolioDevSplit,
  DevMixTrendChip,
};
